import * as THREE from 'three';
import type { Engine } from '../core/Engine';
import type { Course } from '../course/Course';
import { GolfBall } from './GolfBall';
import { TerrainSurfaceProbe } from './TerrainSurfaceProbe';
import { GameplayWind } from './WindModel';
import { ShotDebugTrail } from './ShotDebugTrail';
import { ShotDebugHud } from './ShotDebugHud';
import { InputManager } from '../input/InputManager';
import type { ShotIntent } from '../input/ShotIntent';
import { analyzeSwing, type SwingMetrics } from './SwingAnalyzer';
import { trajectoryForClub, type TrajectoryId } from './Trajectory';
import { CLUBS, clubAtOffset, type ClubDefinition } from './Club';
import { calibrateLaunchConditions } from './LaunchCalibration';
import { applyLieEffects } from './LieEffects';
import { SlopeReadOverlay } from './SlopeReadOverlay';
import { LandingZonePreview } from './LandingZonePreview';
import { expectedCarryMeters } from './Club';
import { SurfaceType } from '../terrain/SurfaceType';
import { AimIndicator } from './AimIndicator';
import { CupPinTarget } from './CupPinTarget';
import { CupCapture } from './CupCapture';
import { PuttLaunchSolver, PuttStrokeAnalyzer } from './PuttLaunchSolver';
import { Units } from '../utils/Units';
import { RoundScore } from './RoundScore';
import { CompetitionRound } from './CompetitionRound';
import { ALEX_CARTER } from './ai/AiGolfer';
import { AiShotPlanner, type AiShotPlan } from './ai/AiShotPlanner';
import { AiExecutionModel, type AiExecutionErrors } from './ai/AiExecutionModel';
import { HazardDetector, WaterHazardResolver } from './HazardResolver';
import { ObstacleCollisionResolver, ObstacleSpatialIndex } from './ObstacleCollision';
import type {
  CareerGameplayCallbacks,
  GameplayCheckpoint,
  GameplayCompetitorConfig,
  GameplaySessionMode,
} from '../gameplay/GameplaySession';
import type { ActiveCareerTournament, SeasonEvent } from '../career/types';

export interface ShotSimulationBuildInfo {
  readonly version: string;
  readonly mode: string;
  readonly controlScheme: 'keyboard + mobile swing';
}

export interface ShotSimulationConfig {
  readonly mode?: GameplaySessionMode;
  readonly player?: GameplayCompetitorConfig;
  readonly pairedOpponent?: GameplayCompetitorConfig;
  readonly roundNumber?: number;
  readonly startingHoleNumber?: number;
  readonly restoredCheckpoint?: GameplayCheckpoint;
  readonly careerCallbacks?: CareerGameplayCallbacks;
  readonly debugHudContainer?: HTMLElement;
  readonly buildInfo?: ShotSimulationBuildInfo;
  readonly showDebugTrail?: boolean;
  readonly tournamentContext?: ActiveCareerTournament;
  readonly tournamentEvent?: SeasonEvent;
}

/**
 * First playable simulation layer: one ball, one player-controlled 7-iron,
 * a camera target, and enough diagnostics to judge whether the shot feels believable.
 */
export class ShotSimulation {
  readonly ball: GolfBall;
  readonly opponentBall: GolfBall;

  private readonly probe: TerrainSurfaceProbe;
  private readonly wind = new GameplayWind(new THREE.Vector3(3.8, 0, -1.8));
  private readonly debugTrail: ShotDebugTrail | null;
  private readonly debugHud: ShotDebugHud | null;
  private teePosition: THREE.Vector3;
  private pinPosition: THREE.Vector3;
  private holeNumber: number;
  private par: number;
  private yardage: number;
  private readonly landingZonePreview = new LandingZonePreview();
  private readonly slopeOverlay: SlopeReadOverlay;
  private readonly aimIndicator = new AimIndicator();
  private readonly cupPin: CupPinTarget;
  private readonly cupCapture = new CupCapture();
  private readonly puttAnalyzer = new PuttStrokeAnalyzer();
  private readonly puttLaunchSolver = new PuttLaunchSolver();

  private unregisterUpdate: (() => void) | null = null;
  private launchStartElapsedSeconds: number | null = null;
  private carryMeters = 0;
  private totalMeters = 0;
  private currentShotStartPosition = new THREE.Vector3();
  private hasRecordedCarry = false;
  private aimAngleRadians = 0;
  private powerPercent = 0;
  private selectedTrajectory: TrajectoryId = 'normal';
  private lastSwingMetrics: SwingMetrics | null = null;
  private elapsedSecondsForHud = 0;
  private selectedClub: ClubDefinition = CLUBS[3];
  private readonly inputManager: InputManager | null;
  private slopeReadEnabled = false;
  private landingZonePreviewEnabled = false;
  private readonly round: RoundScore;
  private readonly competitionRound: CompetitionRound;
  private readonly aiPlanner = new AiShotPlanner();
  private readonly aiExecution = new AiExecutionModel();
  private strokes = 0;
  private holed = false;
  private holeOutAnimating = false;
  private wasOnGreen = false;
  private manualClubOverride = false;
  private previousBallPosition = new THREE.Vector3();
  private transitionSecondsRemaining = 0;
  private opponentLaunchStartElapsedSeconds: number | null = null;
  private opponentCarryMeters = 0;
  private opponentTotalMeters = 0;
  private opponentShotStartPosition = new THREE.Vector3();
  private opponentPreviousBallPosition = new THREE.Vector3();
  private opponentHoleOutAnimating = false;
  private opponentLastPlan: AiShotPlan | null = null;
  private opponentLastErrors: AiExecutionErrors | null = null;
  private opponentResultSeconds = 0;
  private hazardFeedback = '';
  private hazardFeedbackSeconds = 0;
  private readonly hazardDetector: HazardDetector;
  private readonly waterHazards: WaterHazardResolver;
  private lastSafePosition = new THREE.Vector3();
  private readonly obstacleCollision: ObstacleCollisionResolver;
  private readonly playerId: string;
  private readonly opponentId: string;
  private readonly careerCallbacks?: CareerGameplayCallbacks;
  private holeCompletionReported = false;

  constructor(
    private readonly engine: Engine,
    private readonly course: Course,
    config: ShotSimulationConfig = {},
  ) {
    this.playerId = config.player?.id ?? 'player';
    this.careerCallbacks = config.careerCallbacks;
    this.opponentId = config.pairedOpponent?.id ?? ALEX_CARTER.id;
    this.round = new RoundScore(course.holes);
    this.competitionRound = new CompetitionRound(course, [
      {
        id: this.playerId,
        name: config.player?.name ?? 'Player',
        type: 'human',
        ballPosition: new THREE.Vector3(),
        currentLie: SurfaceType.Tee,
        currentHoleStrokes: 0,
        perHoleScores: course.holes.map(() => null),
        totalScore: 0,
        holed: false,
        active: true,
      },
      {
        id: this.opponentId,
        name: config.pairedOpponent?.name ?? ALEX_CARTER.name,
        type: 'ai',
        skillProfile: ALEX_CARTER.skillProfile,
        ballPosition: new THREE.Vector3(),
        currentLie: SurfaceType.Tee,
        currentHoleStrokes: 0,
        perHoleScores: course.holes.map(() => null),
        totalScore: 0,
        holed: false,
        active: true,
      },
    ]);
    const startingHole = Math.max(1, Math.min(18, config.startingHoleNumber ?? 1));
    if (startingHole > 1) {
      const index = this.round.holes.findIndex((item) => item.holeNumber === startingHole);
      if (index >= 0) (this.round as unknown as { activeIndex: number }).activeIndex = index;
    }
    const hole = course.getHole(startingHole);
    this.holeNumber = hole?.number ?? 1;
    this.par = hole?.par ?? 4;
    this.yardage = hole?.yardage ?? 410;
    this.teePosition = hole?.primaryTee.position.clone() ?? new THREE.Vector3(0, 0, 0);
    this.pinPosition =
      hole?.activePin.position.clone() ??
      this.teePosition.clone().add(new THREE.Vector3(0, 0, 100));
    this.aimAngleRadians = Math.atan2(
      this.pinPosition.x - this.teePosition.x,
      this.pinPosition.z - this.teePosition.z,
    );
    this.probe = new TerrainSurfaceProbe(course.terrain);
    this.hazardDetector = new HazardDetector(course);
    this.waterHazards = new WaterHazardResolver(this.hazardDetector);
    const obstacleIndex = new ObstacleSpatialIndex();
    course.obstacleColliders.forEach((collider) => obstacleIndex.insert(collider));
    this.obstacleCollision = new ObstacleCollisionResolver(obstacleIndex);
    const sample = this.probe.sample(this.teePosition.x, this.teePosition.z);
    this.ball = new GolfBall(
      new THREE.Vector3(this.teePosition.x, sample.height + 0.02135, this.teePosition.z),
    );
    this.currentShotStartPosition.copy(this.ball.state.position);
    this.previousBallPosition.copy(this.ball.state.position);
    this.opponentBall = new GolfBall(
      new THREE.Vector3(this.teePosition.x + 0.35, sample.height + 0.02135, this.teePosition.z),
      0x65d6ff,
    );
    this.opponentShotStartPosition.copy(this.opponentBall.state.position);
    this.opponentPreviousBallPosition.copy(this.opponentBall.state.position);
    this.competitionRound.updateBall(this.playerId, this.ball.state.position, SurfaceType.Tee);
    this.competitionRound.updateBall(
      this.opponentId,
      this.opponentBall.state.position,
      SurfaceType.Tee,
    );
    this.engine.scene.add(this.ball.object3D);
    this.engine.scene.add(this.opponentBall.object3D);
    this.engine.scene.add(this.landingZonePreview.object3D);
    this.slopeOverlay = new SlopeReadOverlay(this.probe);
    this.engine.scene.add(this.slopeOverlay.object3D);
    const pinSample = this.probe.sample(this.pinPosition.x, this.pinPosition.z);
    this.pinPosition.y = pinSample.height;
    this.cupPin = new CupPinTarget(this.pinPosition);
    this.cupPin.setGroundHeight(pinSample.height);
    this.engine.scene.add(this.cupPin.object3D);
    this.engine.scene.add(this.aimIndicator.object3D);

    this.debugTrail = (config.showDebugTrail ?? true) ? new ShotDebugTrail() : null;
    if (this.debugTrail) this.engine.scene.add(this.debugTrail.object3D);
    this.inputManager = config.debugHudContainer
      ? new InputManager(config.debugHudContainer)
      : null;
    this.debugHud = config.debugHudContainer
      ? new ShotDebugHud(
          config.debugHudContainer,
          () => this.restartHole(),
          () => this.selectClubOffset(-1),
          () => this.selectClubOffset(1),
          () => this.toggleSlopeRead(),
          () => this.toggleLandingZonePreview(),
          () => this.advanceToNextHole(),
          () => this.restartRound(),
          (trajectory) => this.selectTrajectory(trajectory),
          config.buildInfo,
          config.tournamentContext && config.tournamentEvent
            ? {
                active: config.tournamentContext,
                event: config.tournamentEvent,
                playerId: this.playerId,
              }
            : undefined,
          (open) => this.inputManager?.setSuspended(open),
        )
      : null;
    this.updateHud();
  }

  openTournamentSheet(tab: 'leaderboard' | 'scorecard' = 'leaderboard'): void {
    this.debugHud?.openTournamentSheet(tab);
  }

  setInputSuspended(suspended: boolean): void {
    this.inputManager?.setSuspended(suspended);
  }

  start(): void {
    if (this.unregisterUpdate) return;
    this.unregisterUpdate = this.engine.onUpdate((deltaSeconds, elapsedSeconds) => {
      this.transitionSecondsRemaining = Math.max(0, this.transitionSecondsRemaining - deltaSeconds);
      this.hazardFeedbackSeconds = Math.max(0, this.hazardFeedbackSeconds - deltaSeconds);
      if (this.hazardFeedbackSeconds === 0) this.hazardFeedback = '';
      this.elapsedSecondsForHud = elapsedSeconds;
      this.updateInput(deltaSeconds, elapsedSeconds);
      this.previousBallPosition.copy(this.ball.state.position);
      this.opponentPreviousBallPosition.copy(this.opponentBall.state.position);
      this.ball.update(this.probe, this.wind, deltaSeconds, elapsedSeconds);
      this.obstacleCollision.resolve(this.ball.state, this.previousBallPosition);
      this.updateHazards();
      this.opponentBall.update(this.probe, this.wind, deltaSeconds, elapsedSeconds);
      this.updateAiTurn(deltaSeconds, elapsedSeconds);
      this.debugTrail?.update(this.ball.state);
      this.updateShotMetrics();
      this.updatePostShotSetup();
      this.landingZonePreview.update(
        this.landingZonePreviewTarget(),
        expectedCarryMeters(this.selectedClub) *
          trajectoryForClub(this.selectedClub, this.selectedTrajectory).speedMultiplier,
        deltaSeconds,
      );
      this.slopeOverlay.update(this.ball.state.position, elapsedSeconds, deltaSeconds);
      this.updateHoleOut();
      this.updateAutomaticClubSelection();
      this.aimIndicator.update(
        this.ball.state.position,
        this.aimDirection(),
        this.isPuttingLie(),
        this.canAcceptShotInput() && !this.holed,
      );
      this.updateHud();
    });
  }

  resetToTee(): void {
    this.round.restartHole();
    this.competitionRound.restartHole();
    this.strokes = 0;
    this.resetBallForCurrentHole();
  }

  restartHole(): void {
    this.round.restartHole();
    this.resetToTee();
  }

  restartRound(): void {
    this.round.restartRound();
    this.competitionRound.restartRound();
    this.setupHole(this.round.activeHoleNumber, true);
  }

  advanceToNextHole(): void {
    this.competitionRound.advanceToNextHole();
    const nextHole = this.round.advanceToNextHole();
    if (nextHole === null) return;
    this.transitionSecondsRemaining = 0.9;
    this.setupHole(nextHole, true);
  }

  /** Development-only test helper for validating the full 18-hole routing quickly. */
  devJumpToHole(holeNumber: number, lie: 'tee' | 'approach' | 'green' = 'tee'): void {
    if (!import.meta.env.DEV) return;
    const index = this.round.holes.findIndex((hole) => hole.holeNumber === holeNumber);
    const hole = this.course.getHole(holeNumber);
    if (index < 0 || !hole) return;
    this.round.restartRound();
    for (let i = 0; i < index; i += 1) this.round.holes[i].completed = true;
    // Internal score state remains intentionally single-player; this hook is only for QA.
    (this.round as unknown as { activeIndex: number }).activeIndex = index;
    this.setupHole(holeNumber, true);
    if (lie === 'tee') return;
    const target =
      lie === 'green' ? this.pinPosition : this.pinPosition.clone().lerp(this.teePosition, 0.18);
    const sample = this.probe.sample(target.x, target.z);
    this.ball.reset(new THREE.Vector3(target.x, sample.height + 0.02135, target.z));
    this.currentShotStartPosition.copy(this.ball.state.position);
    this.previousBallPosition.copy(this.ball.state.position);
    this.lastSafePosition.copy(this.ball.state.position);
    this.opponentShotStartPosition.copy(this.opponentBall.state.position);
    this.opponentPreviousBallPosition.copy(this.opponentBall.state.position);
    this.competitionRound.updateBall(this.playerId, this.ball.state.position, SurfaceType.Tee);
    this.competitionRound.updateBall(
      this.opponentId,
      this.opponentBall.state.position,
      SurfaceType.Tee,
    );
    this.updateHud();
  }

  private setupHole(holeNumber: number, resetScoreState: boolean): void {
    this.holeCompletionReported = false;
    const hole = this.course.getHole(holeNumber);
    if (!hole) return;
    this.holeNumber = hole.number;
    this.par = hole.par;
    this.yardage = hole.yardage;
    this.teePosition = hole.primaryTee.position.clone();
    this.pinPosition = hole.activePin.position.clone();
    const pinSample = this.probe.sample(this.pinPosition.x, this.pinPosition.z);
    this.pinPosition.y = pinSample.height;
    this.cupPin.setPosition(this.pinPosition);
    this.cupPin.setGroundHeight(pinSample.height);
    this.aimAngleRadians = Math.atan2(
      this.pinPosition.x - this.teePosition.x,
      this.pinPosition.z - this.teePosition.z,
    );
    if (resetScoreState) this.strokes = this.round.activeHole.strokes;
    this.resetBallForCurrentHole();
  }

  private resetBallForCurrentHole(): void {
    const sample = this.probe.sample(this.teePosition.x, this.teePosition.z);
    this.ball.reset(
      new THREE.Vector3(this.teePosition.x, sample.height + 0.02135, this.teePosition.z),
    );
    this.opponentBall.reset(
      new THREE.Vector3(this.teePosition.x + 0.35, sample.height + 0.02135, this.teePosition.z),
    );
    this.launchStartElapsedSeconds = null;
    this.carryMeters = 0;
    this.totalMeters = 0;
    this.hasRecordedCarry = false;
    this.currentShotStartPosition.copy(this.ball.state.position);
    this.powerPercent = 0;
    this.holed = false;
    this.holeOutAnimating = false;
    this.wasOnGreen = false;
    this.manualClubOverride = false;
    this.slopeReadEnabled = false;
    this.landingZonePreviewEnabled = false;
    this.previousBallPosition.copy(this.ball.state.position);
    this.lastSafePosition.copy(this.ball.state.position);
    this.opponentShotStartPosition.copy(this.opponentBall.state.position);
    this.opponentPreviousBallPosition.copy(this.opponentBall.state.position);
    this.competitionRound.updateBall(this.playerId, this.ball.state.position, SurfaceType.Tee);
    this.competitionRound.updateBall(
      this.opponentId,
      this.opponentBall.state.position,
      SurfaceType.Tee,
    );
    this.autoSuggestClub();
    this.debugTrail?.reset();
    this.updateHud();
  }

  dispose(): void {
    this.unregisterUpdate?.();
    this.unregisterUpdate = null;
    this.engine.scene.remove(this.ball.object3D);
    this.engine.scene.remove(this.opponentBall.object3D);
    this.engine.scene.remove(this.landingZonePreview.object3D);
    this.engine.scene.remove(this.slopeOverlay.object3D);
    this.engine.scene.remove(this.cupPin.object3D);
    this.engine.scene.remove(this.aimIndicator.object3D);
    this.ball.dispose();
    this.opponentBall.dispose();
    if (this.debugTrail) {
      this.engine.scene.remove(this.debugTrail.object3D);
      this.debugTrail.dispose();
    }
    this.landingZonePreview.dispose();
    this.slopeOverlay.dispose();
    this.cupPin.dispose();
    this.aimIndicator.dispose();
    this.inputManager?.dispose();
    this.debugHud?.dispose();
  }

  private updateHazards(): void {
    const ballSample = this.probe.sample(this.ball.state.position.x, this.ball.state.position.z);
    const isOverWater = this.hazardDetector.isWater(this.ball.state.position);
    if (!isOverWater) {
      if (Number.isFinite(ballSample.height)) this.lastSafePosition.copy(this.ball.state.position);
      return;
    }
    const result = this.waterHazards.check(
      this.ball.state,
      this.currentShotStartPosition,
      ballSample.height,
    );
    if (!result) return;
    const sample = this.probe.sample(result.dropPosition.x, result.dropPosition.z);
    this.ball.state.position.y = sample.height + 0.02135;
    for (let i = 0; i < result.penaltyStrokes; i++) {
      this.round.recordStroke();
      this.competitionRound.recordStroke(this.playerId);
    }
    this.strokes = this.round.activeHole.strokes;
    this.hazardFeedback = result.message;
    this.hazardFeedbackSeconds = 3.5;
    this.launchStartElapsedSeconds = null;
    this.powerPercent = 0;
    this.currentShotStartPosition.copy(this.ball.state.position);
    this.aimAtPinFromCurrentLie();
  }

  private updateInput(deltaSeconds: number, elapsedSeconds: number): void {
    const canAcceptShotInput = this.canAcceptShotInput();
    this.inputManager?.update(deltaSeconds, elapsedSeconds, canAcceptShotInput);
    if (this.inputManager?.consumeResetRequested()) this.resetToTee();
    if (this.inputManager?.consumeSlopeReadToggleRequested()) this.toggleSlopeRead();
    if (this.inputManager?.consumeLandingZoneToggleRequested()) this.toggleLandingZonePreview();
    if (canAcceptShotInput) {
      this.aimAngleRadians += this.inputManager?.consumeAimDeltaRadians() ?? 0;
      this.consumeClubInput();
      const shotIntent = this.inputManager?.consumeShotIntent();
      if (shotIntent?.launchCommand) this.launchFromIntent(shotIntent, elapsedSeconds);
      else this.powerPercent = this.inputManager?.powerPercent() ?? 0;
    } else {
      this.inputManager?.consumeShotIntent();
    }
    if (
      !canAcceptShotInput &&
      this.launchStartElapsedSeconds !== null &&
      elapsedSeconds - this.launchStartElapsedSeconds > 0.2
    ) {
      this.powerPercent = 0;
    }
  }

  private consumeClubInput(): void {
    const delta = this.inputManager?.consumeClubDelta() ?? 0;
    if (delta === 0) return;
    if (Math.abs(delta) >= 100) {
      const index = Math.abs(delta) / 100 - 1;
      this.selectedClub = CLUBS[index] ?? this.selectedClub;
      this.manualClubOverride = true;
      return;
    }
    this.selectClubOffset(delta);
  }

  private selectClubOffset(offset: number): void {
    if (!this.canAcceptShotInput()) return;
    this.selectedClub = clubAtOffset(this.selectedClub, offset);
    this.manualClubOverride = true;
    this.powerPercent = 0;
    this.updateHud();
  }

  private canAcceptShotInput(): boolean {
    return (
      this.ball.state.isResting &&
      this.launchStartElapsedSeconds === null &&
      !this.holed &&
      !this.holeOutAnimating &&
      this.competitionRound.canHumanSwing(this.playerId)
    );
  }

  private launchFromIntent(intent: ShotIntent, elapsedSeconds: number): void {
    this.launchStartElapsedSeconds = elapsedSeconds;
    this.manualClubOverride = false;
    this.round.recordStroke();
    this.competitionRound.recordStroke(this.playerId);
    this.strokes = this.round.activeHole.strokes;
    this.hasRecordedCarry = false;
    this.carryMeters = 0;
    this.totalMeters = 0;
    this.debugTrail?.reset();

    this.currentShotStartPosition.copy(this.ball.state.position);
    this.lastSafePosition.copy(this.ball.state.position);
    this.waterHazards.reset();
    this.powerPercent = intent.power;
    const club = this.selectedClub;
    if (club.id === 'putter') {
      this.launchPutt(intent);
      return;
    }
    const trajectory = trajectoryForClub(club, this.selectedTrajectory);
    const swing = analyzeSwing(intent.path, intent.power);
    this.lastSwingMetrics = swing;
    const qualityScalar = THREE.MathUtils.clamp(
      swing.quality / trajectory.difficulty + club.launch.tempoBoost * swing.tempoScore,
      0.3,
      1.05,
    );
    const surface = this.probe.sample(
      this.ball.state.position.x,
      this.ball.state.position.z,
    ).properties;
    const lieUncertainty = surface.lieEffect.dispersionMultiplier;
    const faceErrorRadians = THREE.MathUtils.degToRad(
      swing.faceDegrees * club.dispersionTendency * lieUncertainty * 0.55,
    );
    const pathRadians = THREE.MathUtils.degToRad(swing.pathDegrees);
    const faceToPathRadians = THREE.MathUtils.degToRad(swing.faceToPathDegrees);
    const attackLaunchDelta =
      swing.strike === 'Thin'
        ? -5
        : swing.strike === 'Fat'
          ? -3
          : swing.strike === 'Topped'
            ? -12
            : 0;
    const baseLaunch = calibrateLaunchConditions({
      club,
      power: intent.power,
      quality: qualityScalar,
      aimRadians: this.aimAngleRadians,
      faceErrorRadians,
      trajectory,
      attackLaunchDeltaDegrees: attackLaunchDelta,
    });

    const launch = applyLieEffects(baseLaunch, surface, this.aimAngleRadians);
    const spinRadiansPerSecond = (launch.backspinRpm * Math.PI * 2) / 60;
    const strikeSpin =
      swing.strike === 'Thin' || swing.strike === 'Topped'
        ? 0.58
        : swing.strike === 'Fat'
          ? 0.72
          : 1;
    const sidespin =
      -faceToPathRadians * club.spin.sidespinSensitivity * 95 * launch.sidespinMultiplier +
      pathRadians * club.spin.sidespinSensitivity * 18;
    const backspin = spinRadiansPerSecond * strikeSpin;
    this.ball.launch(launch.velocity, new THREE.Vector3(-backspin, sidespin, 0), {
      rolloutResistanceMultiplier: launch.rolloutResistanceMultiplier,
      bounceRestitutionMultiplier: launch.bounceRestitutionMultiplier,
    });
  }

  private launchPutt(intent: ShotIntent): void {
    const sample = this.probe.sample(this.ball.state.position.x, this.ball.state.position.z);
    const direction = this.aimDirection().projectOnPlane(sample.normal);
    if (direction.lengthSq() < 0.001) direction.copy(this.aimDirection());
    direction.normalize();
    const metrics = this.puttAnalyzer.analyze(intent.path, this.aimAngleRadians);
    const velocity = this.puttLaunchSolver
      .solve(metrics, this.aimAngleRadians)
      .projectOnPlane(sample.normal);
    if (velocity.lengthSq() < 0.0001)
      velocity.copy(direction.multiplyScalar(metrics.initialBallSpeed));
    this.ball.putt(velocity);
  }

  currentPinPosition(): THREE.Vector3 {
    return this.pinPosition.clone();
  }

  activeCameraBall(): GolfBall {
    return this.competitionRound.activeTurnCompetitorId === this.opponentId
      ? this.opponentBall
      : this.ball;
  }

  activeAimDirection(): THREE.Vector3 {
    if (this.competitionRound.activeTurnCompetitorId !== this.opponentId)
      return this.aimDirection();
    if (this.opponentLastPlan) {
      const toTarget = this.opponentLastPlan.target
        .clone()
        .sub(this.opponentBall.state.position)
        .setY(0);
      if (toTarget.lengthSq() > 0.001) return toTarget.normalize();
    }
    const toPin = this.pinPosition.clone().sub(this.opponentBall.state.position).setY(0);
    if (toPin.lengthSq() < 0.001) toPin.set(0, 0, 1);
    return toPin.normalize();
  }

  aimDirection(): THREE.Vector3 {
    return new THREE.Vector3(
      Math.sin(this.aimAngleRadians),
      0,
      Math.cos(this.aimAngleRadians),
    ).normalize();
  }

  private updateShotMetrics(): void {
    const snapshot = this.ball.state.snapshot();
    const fromTee = snapshot.position.clone().sub(this.teePosition).setY(0);
    this.totalMeters = Math.max(0, fromTee.dot(this.initialHoleDirection()));
    const fromShotStart = snapshot.position.clone().sub(this.currentShotStartPosition).setY(0);
    const shotDistanceMeters = Math.max(0, fromShotStart.dot(this.aimDirection()));
    if (
      !this.hasRecordedCarry &&
      this.launchStartElapsedSeconds !== null &&
      snapshot.altitude <= 0.05 &&
      shotDistanceMeters > 5
    ) {
      this.carryMeters = shotDistanceMeters;
      this.hasRecordedCarry = true;
    }
    if (!this.hasRecordedCarry) this.carryMeters = shotDistanceMeters;
  }

  private updatePostShotSetup(): void {
    if (this.launchStartElapsedSeconds === null || !this.ball.state.isResting) return;
    this.launchStartElapsedSeconds = null;
    this.powerPercent = 0;
    this.landingZonePreviewEnabled = false;
    this.aimAtPinFromCurrentLie();
    this.currentShotStartPosition.copy(this.ball.state.position);
    this.competitionRound.updateBall(
      this.playerId,
      this.ball.state.position,
      this.ball.state.surfaceType,
    );
    this.competitionRound.chooseNextTurn();
    this.autoSuggestClub();
  }

  private aimAtPinFromCurrentLie(): void {
    const toPin = this.pinPosition.clone().sub(this.ball.state.position).setY(0);
    if (toPin.lengthSq() < 0.001) return;
    this.aimAngleRadians = Math.atan2(toPin.x, toPin.z);
  }

  private initialHoleDirection(): THREE.Vector3 {
    const direction = this.pinPosition.clone().sub(this.teePosition).setY(0);
    if (direction.lengthSq() < 0.001) direction.set(0, 0, 1);
    return direction.normalize();
  }

  toggleSlopeRead(): void {
    this.slopeReadEnabled = !this.slopeReadEnabled;
    this.slopeOverlay.setVisible(this.slopeReadEnabled);
  }

  toggleLandingZonePreview(): void {
    this.landingZonePreviewEnabled = !this.landingZonePreviewEnabled;
  }

  isGreenReadEnabled(): boolean {
    return this.slopeReadEnabled;
  }

  landingZonePreviewTarget(): THREE.Vector3 | null {
    if (!this.landingZonePreviewEnabled || !this.canAcceptShotInput() || this.isPuttingLie())
      return null;
    const trajectory = trajectoryForClub(this.selectedClub, this.selectedTrajectory);
    const carry =
      expectedCarryMeters(this.selectedClub) *
      trajectory.speedMultiplier *
      (1 +
        this.wind
          .sample(this.ball.state.position, this.elapsedSecondsForHud)
          .dot(this.aimDirection()) *
          0.015 *
          trajectory.windExposure);
    const target = this.ball.state.position.clone().addScaledVector(this.aimDirection(), carry);
    const sample = this.probe.sample(target.x, target.z);
    target.y = sample.height;
    return target;
  }

  isPuttingLie(): boolean {
    return this.activeCameraBall().state.surfaceType === SurfaceType.Green;
  }

  private updateAutomaticClubSelection(): void {
    const isOnGreen = this.ball.state.surfaceType === SurfaceType.Green;
    if (!this.canAcceptShotInput() || !isOnGreen) {
      this.wasOnGreen = isOnGreen;
      return;
    }

    if (!this.wasOnGreen) {
      this.slopeReadEnabled = true;
      this.slopeOverlay.setVisible(true);
    }
    this.autoSuggestClub();
    this.wasOnGreen = true;
  }

  private autoSuggestClub(): void {
    if (!this.canAcceptShotInput() || this.manualClubOverride) return;
    const distanceYards = this.distanceToPinYards();
    const surface = this.ball.state.surfaceType;
    const suggested = this.suggestClub(distanceYards, surface);
    if (suggested) this.selectedClub = suggested;
  }

  private suggestClub(distanceYards: number, surface: SurfaceType): ClubDefinition | null {
    if (surface === SurfaceType.Green || distanceYards <= 18) {
      return CLUBS.find((club) => club.id === 'putter') ?? null;
    }
    const playableClubs = CLUBS.filter((club) => club.id !== 'putter');
    const liePenalty =
      surface === SurfaceType.Bunker ? 0.68 : surface === SurfaceType.Rough ? 0.82 : 1;
    return playableClubs.reduce((best, club) => {
      const bestGap = Math.abs(best.expectedCarryYards * liePenalty - distanceYards);
      const clubGap = Math.abs(club.expectedCarryYards * liePenalty - distanceYards);
      return clubGap < bestGap ? club : best;
    }, playableClubs[0]);
  }

  private updateHoleOut(): void {
    if (this.holed) return;
    if (this.holeOutAnimating) {
      if (this.ball.isCupDropComplete()) {
        this.holed = true;
        this.competitionRound.markHoled(this.playerId);
        this.round.activeHole.strokes = this.competitionRound.competitor(
          this.playerId,
        ).currentHoleStrokes;
        if (this.competitionRound.roundState !== 'playing') {
          this.round.completeActiveHole();
          this.reportCareerHole();
        }
      }
      return;
    }
    const result = this.cupCapture.evaluate(
      this.previousBallPosition,
      this.ball.state,
      this.pinPosition,
    );
    if (result === 'capture') {
      this.holeOutAnimating = true;
      this.ball.captureIntoCup(
        new THREE.Vector3(this.pinPosition.x, this.pinPosition.y, this.pinPosition.z),
      );
      this.launchStartElapsedSeconds = null;
      this.powerPercent = 0;
      this.landingZonePreviewEnabled = false;
      this.slopeReadEnabled = false;
      this.slopeOverlay.setVisible(false);
    }
  }

  private updateAiTurn(deltaSeconds: number, elapsedSeconds: number): void {
    this.opponentResultSeconds = Math.max(0, this.opponentResultSeconds - deltaSeconds);
    this.updateOpponentShotMetrics();
    this.updateOpponentHoleOut();
    if (this.competitionRound.roundState !== 'playing') return;
    const ai = this.competitionRound.competitor(this.opponentId);
    if (this.competitionRound.activeTurnCompetitorId !== this.opponentId || ai.holed) return;
    if (this.opponentLaunchStartElapsedSeconds !== null) {
      if (this.opponentBall.state.isResting) {
        this.opponentLaunchStartElapsedSeconds = null;
        this.opponentResultSeconds = 3.2;
        this.competitionRound.updateBall(
          this.opponentId,
          this.opponentBall.state.position,
          this.opponentBall.state.surfaceType,
        );
        this.competitionRound.chooseNextTurn();
      }
      return;
    }
    if (!this.opponentBall.state.isResting || this.opponentHoleOutAnimating) return;
    const plan = this.aiPlanner.plan({
      ballPosition: this.opponentBall.state.position,
      currentLie: this.opponentBall.state.surfaceType,
      hole: this.competitionRound.activeHole,
      wind: this.wind,
      skill: ALEX_CARTER.skillProfile,
      shotNumber: ai.currentHoleStrokes + 1,
      elapsedSeconds,
    });
    const launch = this.aiExecution.createLaunch(
      this.opponentBall.state.position,
      plan,
      ALEX_CARTER.skillProfile,
      this.opponentBall.state.surfaceType,
      this.probe,
      elapsedSeconds * 0.137 + ai.currentHoleStrokes,
    );
    this.opponentLastPlan = plan;
    this.opponentLastErrors = launch.errors;
    if (import.meta.env.DEV)
      console.debug('AI diagnostics', { plan, errors: this.opponentLastErrors });
    this.competitionRound.recordStroke(this.opponentId);
    this.opponentLaunchStartElapsedSeconds = elapsedSeconds;
    this.opponentCarryMeters = 0;
    this.opponentTotalMeters = 0;
    this.opponentShotStartPosition.copy(this.opponentBall.state.position);
    if (launch.putt) this.opponentBall.putt(launch.velocity, launch.spin);
    else this.opponentBall.launch(launch.velocity, launch.spin, launch.options);
  }

  private updateOpponentShotMetrics(): void {
    const fromShotStart = this.opponentBall.state.position
      .clone()
      .sub(this.opponentShotStartPosition)
      .setY(0);
    this.opponentTotalMeters = fromShotStart.length();
    if (this.opponentBall.state.altitude <= 0.05 || this.opponentCarryMeters === 0)
      this.opponentCarryMeters = this.opponentTotalMeters;
  }

  private updateOpponentHoleOut(): void {
    const ai = this.competitionRound.competitor(this.opponentId);
    if (ai.holed) return;
    if (this.opponentHoleOutAnimating) {
      if (this.opponentBall.isCupDropComplete()) {
        this.opponentHoleOutAnimating = false;
        this.competitionRound.markHoled(this.opponentId);
        this.opponentResultSeconds = 3.2;
        if (this.competitionRound.roundState !== 'playing') {
          this.round.completeActiveHole();
          this.reportCareerHole();
        }
      }
      return;
    }
    const result = this.cupCapture.evaluate(
      this.opponentPreviousBallPosition,
      this.opponentBall.state,
      this.pinPosition,
    );
    if (result === 'capture') {
      this.opponentHoleOutAnimating = true;
      this.opponentLaunchStartElapsedSeconds = null;
      this.opponentBall.captureIntoCup(
        new THREE.Vector3(this.pinPosition.x, this.pinPosition.y, this.pinPosition.z),
      );
    }
  }

  private opponentDistanceToPinYards(): number {
    return Units.metersToYards(
      this.opponentBall.state.position.clone().sub(this.pinPosition).setY(0).length(),
    );
  }

  private reportCareerHole(): void {
    if (this.holeCompletionReported || !this.careerCallbacks) return;
    this.holeCompletionReported = true;
    this.careerCallbacks.onHoleCompleted({
      playerScore: this.competitionRound.competitor(this.playerId).currentHoleStrokes,
      opponentScore: this.competitionRound.competitor(this.opponentId).currentHoleStrokes,
      par: this.par,
    });
  }

  private distanceToPinYards(): number {
    return Units.metersToYards(
      this.ball.state.position.clone().sub(this.pinPosition).setY(0).length(),
    );
  }

  private selectTrajectory(trajectory: TrajectoryId): void {
    if (!this.canAcceptShotInput() || this.selectedClub.id === 'putter') return;
    this.selectedTrajectory = trajectory;
    this.updateHud();
  }

  private feedbackLabel(): string {
    if (this.hazardFeedback) return this.hazardFeedback;
    const swing = this.lastSwingMetrics;
    if (!swing) return '';
    if (Math.abs(swing.faceDegrees) > 6 && Math.abs(swing.faceToPathDegrees) < 3)
      return swing.faceDegrees > 0 ? 'Pushed' : 'Pulled';
    if (swing.faceToPathDegrees < -8) return 'Hook';
    if (swing.faceToPathDegrees > 8) return 'Slice';
    if (swing.faceToPathDegrees < -2.2) return 'Draw';
    if (swing.faceToPathDegrees > 2.2) return 'Fade';
    return swing.strike;
  }

  private updateHud(): void {
    const snapshot = this.ball.state.snapshot();
    this.debugHud?.update(
      this.debugHud.snapshotToState(snapshot, {
        aimDegrees: THREE.MathUtils.radToDeg(this.aimAngleRadians),
        powerPercent: this.powerPercent,
        carryMeters: this.carryMeters,
        totalMeters: this.totalMeters,
        selectedClub: this.selectedClub,
        waitingForShot: this.launchStartElapsedSeconds === null,
        slopeReadEnabled: this.slopeReadEnabled,
        landingZonePreviewEnabled: this.landingZonePreviewEnabled,
        distanceToPinYards: this.distanceToPinYards(),
        strokes: this.strokes,
        holed: this.holed,
        holeOutAnimating: this.holeOutAnimating,
        holeNumber: this.holeNumber,
        par: this.par,
        yardage: this.yardage,
        roundStatus: this.round.status,
        isFinalHole: this.round.isFinalHole,
        resultLabel: this.round.resultLabel(this.round.activeHole),
        scoreRelativeToPar: this.round.relativeToPar(this.round.activeHole),
        scorecard: this.round.holes,
        totalStrokes: this.round.totalStrokes(),
        totalPar: this.round.totalPar(),
        totalRelativeToPar: this.round.completedTotalRelativeToPar(),
        transitionActive: this.transitionSecondsRemaining > 0,
        trajectory: trajectoryForClub(this.selectedClub, this.selectedTrajectory),
        selectedTrajectory: this.selectedTrajectory,
        wind: this.wind.snapshot(this.aimAngleRadians, this.elapsedSecondsForHud),
        shotFeedback: this.feedbackLabel(),
        currentCompetitorName: this.competitionRound.activeTurn.name,
        currentTurnType: this.competitionRound.activeTurn.type === 'ai' ? 'ai' : 'human',
        opponentName: ALEX_CARTER.name,
        opponentScore: this.competitionRound.competitor(this.opponentId).totalScore,
        opponentCurrentHoleStrokes: this.competitionRound.competitor(this.opponentId)
          .currentHoleStrokes,
        opponentRelativeToPar:
          this.competitionRound.competitor(this.opponentId).totalScore -
          this.round.holes
            .filter((hole) => hole.completed)
            .reduce((sum, hole) => sum + hole.par, 0),
        opponentDistanceToPinYards: this.opponentDistanceToPinYards(),
        opponentLie: this.opponentBall.state.surfaceType,
        opponentResult:
          this.opponentResultSeconds > 0 && this.opponentLastPlan
            ? `${ALEX_CARTER.name} · ${this.opponentLastPlan.club.name} · ${Units.metersToYards(this.opponentTotalMeters).toFixed(0)} yd — ${this.opponentBall.state.surfaceType}`
            : '',
        opponentScores: this.competitionRound.competitor(this.opponentId).perHoleScores,
        playerScores: this.competitionRound.competitor(this.playerId).perHoleScores,
      }),
    );
  }
}
